'use client';
import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Typography, AppImage, DotsIndicator } from '@workspace/ui/common';
import type { LogoWallProps } from './logo-wall.types';
import { cn } from '@workspace/lib';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { stripInvisibles } from '@workspace/constants';
import { getBackgroundClass } from '@workspace/lib';
import { sanityImageSrc } from '@/sanity/lib/image';
import { getTextAlignClass } from './layout';
import { useMediaQuery } from '@workspace/hooks/commons/useMediaQuery';

export function LogoWall({ block }: LogoWallProps) {
  const { backgroundColor, heading, description, logos, textAlignment } = block;
  const textAlignClass = getTextAlignClass(textAlignment);
  const backgroundClass = getBackgroundClass(backgroundColor);

  const originalLogos = Array.isArray(logos) ? logos : [];
  const isMobile = useMediaQuery('(max-width: 992px)');

  // Determine if carousel should be disabled based on screen size
  const shouldDisableCarousel = isMobile ? originalLogos.length <= 3 : originalLogos.length <= 5;

  let displayLogos = originalLogos;

  // Only duplicate if we have enough logos and carousel is enabled
  if (!shouldDisableCarousel && originalLogos.length > 5) {
    const repeatCount = Math.ceil(12 / originalLogos.length);
    displayLogos = Array(repeatCount).fill(originalLogos).flat();
  }

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: !shouldDisableCarousel && originalLogos.length >= 5,
    align: shouldDisableCarousel ? 'center' : 'start',
    containScroll: shouldDisableCarousel ? 'trimSnaps' : false,
    dragFree: !shouldDisableCarousel,
    slidesToScroll: 1,
    watchDrag: !shouldDisableCarousel,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const lenRef = React.useRef(originalLogos.length);
  lenRef.current = originalLogos.length;

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap() % lenRef.current);
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  if (!displayLogos.length) return null;

  return (
    <div className={cn('w-full', backgroundClass)}>
      <div className="porta-container flex flex-col items-center gap-6 md:gap-12 px-6 lg:pt-20 py-8 lg:px-30">
        <div className={cn('flex flex-col gap-2 w-full', textAlignClass)}>
          {heading && (
            <Typography variant="h2" styleVariant="h3" className={cn('font-bold', textAlignClass)}>
              {heading}
            </Typography>
          )}

          {Array.isArray(description) && (
            <div className={cn('text-sm! leading-[1.42857] text-secondary-600', textAlignClass)}>
              <CustomPortableText value={description} />
            </div>
          )}
        </div>

        {/* Logos Carousel */}
        <div className="flex justify-center w-full">
          <div className="embla w-full max-w-full overflow-hidden" ref={emblaRef}>
            <div className={cn('embla__container flex items-center gap-4', shouldDisableCarousel && 'justify-center')}>
              {displayLogos.map((logo, index) => {
                if (!logo?.url) return null;
                return (
                  <div
                    key={`${logo._key || 'logo'}-${index}`}
                    className="embla__slide grow-0 shrink-0 basis-1/3 md:basis-1/5 max-md:flex max-md:justify-center"
                  >
                    <div className="flex items-center justify-center relative lg:w-full w-fit">
                      <AppImage
                        src={sanityImageSrc(logo.url)}
                        alt={stripInvisibles(logo.alt) || 'Logo'}
                        className={cn(
                          'w-full max-w-25 sm:max-w-48 md:max-w-53.5',
                          'h-8 sm:h-24 md:h-27.5',
                          'md:p-8',
                          'object-contain!',
                        )}
                        loading="lazy"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {!shouldDisableCarousel && (
          <DotsIndicator
            className="justify-center pt-md md:pt-xl"
            count={originalLogos.length}
            currentIndex={selectedIndex}
            onDotClick={scrollTo}
          />
        )}
      </div>
    </div>
  );
}

export default LogoWall;
